import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const INTRO_SRC = "/media/intro.mp4";
const TIMEOUT_MS = 12000;

export default function Intro({ onComplete }) {
    const [exiting, setExiting] = useState(false);
    const videoRef = useRef(null);
    const doneRef = useRef(false);
    const timers = useRef([]);

    const finish = () => {
        if (doneRef.current) return;
        doneRef.current = true;
        setExiting(true);
        timers.current.push(setTimeout(() => onComplete("end"), 1150));
    };

    useEffect(() => {
        const prev = document.documentElement.style.overflow;
        document.documentElement.style.overflow = "hidden";
        timers.current.push(setTimeout(finish, TIMEOUT_MS));
        return () => {
            document.documentElement.style.overflow = prev;
            timers.current.forEach(clearTimeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;

        // Explicitly set the muted DOM attribute — React's JSX `muted` prop sets
        // the JS property but not the HTML attribute; Safari checks the attribute.
        v.muted = true;
        v.setAttribute("muted", "");

        const onEnded = () => finish();
        v.addEventListener("ended", onEnded);

        // Explicit play() call — belt-and-suspenders for Safari
        v.play().catch(() => {});

        return () => {
            v.removeEventListener("ended", onEnded);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className="fixed inset-0 z-[100] overflow-hidden bg-void"
            data-testid="intro-overlay"
        >
            {/* ── Video ── */}
            <video
                ref={(el) => { videoRef.current = el; }}
                src={INTRO_SRC}
                autoPlay
                muted
                playsInline
                preload="auto"
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    zIndex: 10,
                    opacity: exiting ? 0 : 1,
                    transition: "opacity 0.6s ease-in",
                }}
            />

            {/* ── Progress bar ── */}
            <div className="absolute inset-x-0 bottom-0 z-20 h-[3px] bg-white/5">
                <motion.div
                    className="h-full bg-neon"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: (TIMEOUT_MS - 1000) / 1000, ease: "linear" }}
                />
            </div>

            {/* ── Skip button ── */}
            <button
                data-testid="intro-skip-btn"
                onClick={finish}
                className="absolute bottom-8 right-6 z-20 rounded-full border border-white/15 bg-black/40 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.3em] text-ivory backdrop-blur transition hover:border-neon hover:text-neon sm:right-10"
            >
                Skip Intro
            </button>

            {/* ── Curtain wipe on exit ── */}
            <div className="pointer-events-none absolute inset-0 z-30 flex">
                {[0, 1, 2].map((i) => (
                    <motion.div
                        key={i}
                        className="h-full flex-1 bg-void"
                        style={{ transformOrigin: "bottom" }}
                        initial={{ scaleY: 0 }}
                        animate={{ scaleY: exiting ? 1 : 0 }}
                        transition={{
                            duration: 0.65,
                            delay: exiting ? i * 0.09 : 0,
                            ease: [0.76, 0, 0.24, 1],
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
